// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Left orange accent stripe for Search/Actions section
// - Inputs: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - IconButtons: blue default, orange on hover
// - No business/API logic changed

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import { API_URL, API_UPLOAD_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ExternalMarksManager = () => {
  // ✅ Theme constants (same palette)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  // ===== Original state/logic =====
  const [data, setData] = useState([]);
  const [newRow, setNewRow] = useState({
    CampusCode: "",
    StudentID: "",
    Semester: "",
    YearPassed: "",
    SubjectCode: "",
    Year: "",
    Internal: "",
    External: "",
    Grade: "",
    Credit: "",
    RES: "",
  });
  const [editRowId, setEditRowId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef();

  // hard-code or derive the list of StudentIDs you want to display
  const studentIds = [];
  const token = localStorage.getItem("auth");

  // decode CampusCode from JWT
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setNewRow((r) => ({ ...r, CampusCode: decoded.campusCode }));
    } catch {
      setError("Failed to decode authentication token");
    }
  }, []);

  // fetch existing marks
  useEffect(() => {
    if (!newRow.CampusCode) return;
    axios
      .get(`${API_URL}/api/ExternalMarks`, {
        params: {
          //CampusCode: newRow.CampusCode,
          StudentIDs: studentIds.join(","),
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setData(res.data))
      .catch(() => setError("Failed to fetch external marks"));
  }, [newRow.CampusCode]);

  const downloadSampleExcel = () => {
    const cc = newRow.CampusCode || "CAMPUS01";

    // Must match your upload columns
    const headers = [
      "CampusCode",
      "StudentID",
      "Semester",
      "YearPassed",
      "SubjectCode",
      "Year",
      "Internal",
      "External",
      "Grade",
      "Credit",
      "RES",
    ];

    // sample rows
    const sampleRows = [
      {
        CampusCode: cc,
        StudentID: "2025KKBTECHCSM970324",
        Semester: "31",
        YearPassed: "202501",
        SubjectCode: "R2031019",
        Year: "2025JANUARY",
        Internal: 25,
        External: 65,
        Grade: "A",
        Credit: 3,
        RES: "P",
      },
      {
        CampusCode: cc,
        StudentID: "2025KKBTECHCSM970324",
        Semester: "21",
        YearPassed: "202502",
        SubjectCode: "R202101A",
        Year: "2025FEBRUARY",
        Internal: 25,
        External: 65,
        Grade: "A",
        Credit: 3,
        RES: "P",
      },
    ];

    // enforce header order
    const normalized = sampleRows.map((r) => {
      const obj = {};
      headers.forEach((h) => (obj[h] = r[h] ?? ""));
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(normalized, { header: headers });

    // column widths
    ws["!cols"] = [
      { wch: 14 }, // CampusCode
      { wch: 20 }, // StudentID
      { wch: 10 }, // Semester
      { wch: 12 }, // YearPassed
      { wch: 12 }, // SubjectCode
      { wch: 14 }, // Year
      { wch: 10 }, // Internal
      { wch: 10 }, // External
      { wch: 10 }, // Grade
      { wch: 8 }, // Credit
      { wch: 8 }, // RES
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ExternalMarks");

    // Optional: Instructions sheet
    const instructions = [
      ["Field", "Notes / Example"],
      ["CampusCode", "Auto-filled from login (don’t change)"],
      ["StudentID", "Student ID (e.g., STU001)"],
      ["Semester", "Numeric semester (11,2,3...)"],
      ["YearPassed", "Pass year (e.g., 202501 , 202502)"],
      ["SubjectCode", "Subject code (e.g., CS101)"],
      ["Year", "Year label (2025JANUARY / 2025FEBRUARY)"],
      ["Internal", "Internal marks (number)"],
      ["External", "External marks (number)"],
      ["Grade", "Grade (e.g., A, B+, C, F)"],
      ["Credit", "Credits (e.g., 3, 4)"],
      ["RES", "Result (P/F) or as per your convention"],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 18 }, { wch: 55 }];
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `ExternalMarks_Sample_${cc}.xlsx`);
  };

  const handleInputChange = (e, id = null) => {
    const { name, value } = e.target;
    if (id == null) {
      setNewRow((r) => ({ ...r, [name]: value }));
    } else {
      setData((d) => d.map((r) => (r.id === id ? { ...r, [name]: value } : r)));
    }
  };

  const handleAddRow = async () => {
    try {
      const { data: created } = await axios.post(`${API_URL}/api/ExternalMarks`, newRow, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData((d) => [...d, created]);
      setNewRow((r) => ({
        ...r,
        StudentID: "",
        Semester: "",
        YearPassed: "",
        SubjectCode: "",
        Year: "",
        Internal: "",
        External: "",
        Grade: "",
        Credit: "",
        RES: "",
      }));
      setSuccess(true);
    } catch {
      setError("Failed to add new row");
    }
  };

  const handleUpdateRow = async (id) => {
    const updated = data.find((r) => r.id === id);
    try {
      await axios.put(`${API_URL}/api/ExternalMarks/${id}`, updated, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditRowId(null);
      setSuccess(true);
    } catch {
      setError("Failed to update row");
    }
  };

  const handleDeleteRow = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/ExternalMarks/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData((d) => d.filter((r) => r.id !== id));
      setSuccess(true);
    } catch {
      setError("Failed to delete row");
    }
  };

  // bulk upload
  const handleBulkClick = () => fileInputRef.current.click();
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", newRow.CampusCode);
    form.append("StudentIDs", studentIds.join(","));
    try {
      await axios.post(`${API_URL}/api/UploadExternalMarks`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      // refresh
      const res = await axios.get(`${API_URL}/api/ExternalMarks`, {
        params: {
          //CampusCode: newRow.CampusCode,
          StudentIDs: studentIds.join(","),
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setSuccess(true);
    } catch {
      setError("Bulk upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const [searchId, setSearchId] = useState("");

  const handleSearch = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ExternalMarks`, {
        params: {
          //CampusCode: newRow.CampusCode,
          StudentIDs: searchId.trim(),
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      setError("Failed to fetch data for the given Student ID");
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update External Marks
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Search by Student ID, bulk upload, or edit rows inline
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={downloadSampleExcel}>
              Download Sample
            </Button>

            <Button variant="contained" sx={primaryBtnSx} startIcon={<UploadIcon />} onClick={handleBulkClick}>
              Bulk Upload
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Search/Actions section with left orange stripe */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Search
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    label="Enter Student ID"
                    value={searchId}
                    onChange={(e) => setSearchId(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ ...inputSx, minWidth: 260 }}
                  />
                  <Button variant="contained" sx={primaryBtnSx} onClick={handleSearch}>
                    Search
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Messages */}
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Table */}
      {/* OUTER – keeps rounded corners & spacing */}
      <Paper elevation={0} sx={tableWrapSx}>

        {/* INNER – ONLY for horizontal scroll */}
        <Box
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <TableContainer component="div">
            <Table size="small" sx={{ width: "100%" }}>
              <colgroup>
                <col style={{ width: "2%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "9%" }} />
              </colgroup>

              <TableHead>
                <TableRow sx={theadRowSx}>
                  {[
                    "#",
                    "Campus",
                    "StudentID",
                    "Semester",
                    "YearPassed",
                    "Subject",
                    "Year",
                    "Internal",
                    "External",
                    "Grade",
                    "Credit",
                    "RES",
                    "Actions",
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {data.map((row, idx) =>
                  editRowId === row.id ? (
                    <TableRow key={row.id} sx={zebraRowSx(idx)}>
                      <TableCell>{idx + 1}</TableCell>
                      {[
                        "CampusCode",
                        "StudentID",
                        "Semester",
                        "YearPassed",
                        "SubjectCode",
                        "Year",
                        "Internal",
                        "External",
                        "Grade",
                        "Credit",
                        "RES",
                      ].map((field) => (
                        <TableCell key={field}>
                          <TextField
                            name={field}
                            value={row[field] ?? ""}
                            onChange={(e) => handleInputChange(e, row.id)}
                            size="small"
                            fullWidth
                            sx={inputSx}
                          />
                        </TableCell>
                      ))}
                      <TableCell>
                        <IconButton sx={iconBtnSx} onClick={() => handleUpdateRow(row.id)} title="Save">
                          <SaveIcon />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={() => setEditRowId(null)} title="Cancel">
                          <CancelIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={row.id} sx={zebraRowSx(idx)}>
                      <TableCell>{idx + 1}</TableCell>
                      {[
                        "CampusCode",
                        "StudentID",
                        "Semester",
                        "YearPassed",
                        "SubjectCode",
                        "Year",
                        "Internal",
                        "External",
                        "Grade",
                        "Credit",
                        "RES",
                      ].map((field) => (
                        <TableCell key={field}>{row[field]}</TableCell>
                      ))}
                      <TableCell>
                        <IconButton sx={iconBtnSx} onClick={() => setEditRowId(row.id)} title="Edit">
                          <EditIcon />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={() => handleDeleteRow(row.id)} title="Delete">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                )}

                {/* new-row form */}
                <TableRow sx={zebraRowSx(data.length)}>
                  <TableCell />
                  {[
                    newRow.CampusCode,
                    newRow.StudentID,
                    newRow.Semester,
                    newRow.YearPassed,
                    newRow.SubjectCode,
                    newRow.Year,
                    newRow.Internal,
                    newRow.External,
                    newRow.Grade,
                    newRow.Credit,
                    newRow.RES,
                  ].map((val, i) =>
                    i === 0 ? (
                      <TableCell key={i}>
                        <TextField value={val} disabled size="small" fullWidth sx={inputSx} />
                      </TableCell>
                    ) : (
                      <TableCell key={i}>
                        <TextField
                          name={
                            [
                              "CampusCode",
                              "StudentID",
                              "Semester",
                              "YearPassed",
                              "SubjectCode",
                              "Year",
                              "Internal",
                              "External",
                              "Grade",
                              "Credit",
                              "RES",
                            ][i]
                          }
                          value={val}
                          onChange={handleInputChange}
                          size="small"
                          fullWidth
                          sx={inputSx}
                        />
                      </TableCell>
                    )
                  )}
                  <TableCell>
                    <IconButton sx={iconBtnSx} onClick={handleAddRow} title="Add">
                      <AddIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default ExternalMarksManager;
